'use client'

import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast'
import { useToast } from '@/hooks/use-toast'

const VARIANT_ICON = {
  default: Info,
  success: CheckCircle2,
  destructive: XCircle,
  warning: AlertTriangle,
} as const

/**
 * The one toast primitive for mutation feedback (`frontend-code-standards.md` §16) — shadcn's
 * classic Radix `Toast` primitive, styled with this app's own success/warning/destructive design
 * tokens rather than sonner's built-in palette. Mounted once in `src/providers/index.tsx`, never
 * per-page.
 */
export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider duration={3000}>
      {toasts.map(({ id, title, description, action, variant, ...props }) => {
        const Icon = VARIANT_ICON[variant ?? 'default']
        return (
          <Toast key={id} variant={variant} {...props}>
            <Icon className="size-5 shrink-0" />
            <div className="grid flex-1 gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && <ToastDescription>{description}</ToastDescription>}
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
